/**
 * The crystal charges: crystals hold a great deal of energy, and bound tight to rock and set
 * humming they shatter it. A dark plate clawed onto the rock, a cluster of crystals leaning out
 * from its middle, held by iron bands, glowing and pulsing; a white-hot heart rising inside the
 * cluster as the fuse burns down; splinters circling it while it hums. The heavy charge is the
 * same, bigger, with more crystals and a ring of ore blocks round its plate for weight. Offsets
 * are in machine space, y up from the tile's top, a tile about 1.2 wide.
 */
import * as THREE from 'three';

import { around, merged, part, type MachinePart } from './part';

const SLATE = 0x3b4150;
const IRON = 0x5d6474;
const VIOLET = 0xb69cff;
const VIOLET_GLOW = 0x9d6bff;
/** The heart: white hot, with the charge's pink (core/palette.ts). */
const HEART = 0xfff1f6;
const HEART_GLOW = 0xe0407f;
const SPLINTER = 0xe7a3ff;
const ORE_BLOCK = 0x7a68a8;

const PLATE = 0.04;

interface ChargeShape {
  /** Unique part names: 'chg', 'hchg'. */
  id: string;
  /** The whole charge's size against the measures below. */
  size: number;
  /** Crystals leaning out round the middle. */
  crystals: number;
  /** Ore blocks round its plate (the heavy charge's weight), or none. */
  weights: number;
}

export interface ChargeParts {
  frame: readonly MachinePart[];
  /** A unit tall from its foot: the props scale it to how far the fuse has burnt. */
  heart: MachinePart;
  heartFull: number;
  splinters: MachinePart;
}

/** Evenly round a circle, from `start`. */
const angles = (n: number, start = 0): number[] =>
  Array.from({ length: n }, (_, k) => start + (k * 2 * Math.PI) / n);

/** A part at the charge's size; the heart keeps its unit height (the props scale it). */
function sized(size: number, p: MachinePart, unitTall = false): MachinePart {
  p.geometry.scale(size, unitTall ? 1 : size, size);
  return { ...p, offset: [p.offset[0] * size, p.offset[1] * size, p.offset[2] * size] };
}

/** The plate and its claws, gripping the rock's edge. */
function plate(): THREE.BufferGeometry {
  return merged([
    new THREE.CylinderGeometry(0.17, 0.2, PLATE, 6).translate(0, PLATE / 2, 0),
    around(
      () => new THREE.BoxGeometry(0.05, 0.07, 0.045).translate(0, PLATE - 0.035, 0),
      angles(3, Math.PI / 6),
      0.19,
    ),
  ]);
}

/** The cluster: a tall crystal in the middle and the rest leaning out round it. */
function cluster(n: number): THREE.BufferGeometry {
  return merged([
    new THREE.OctahedronGeometry(0.06).scale(0.8, 2.6, 0.8).translate(0, 0.16, 0),
    around(
      () =>
        new THREE.OctahedronGeometry(0.055)
          .scale(0.75, 2.1, 0.75)
          .translate(0, 0.1, 0)
          .rotateZ(-0.38),
      angles(n),
      0.07,
    ),
  ]);
}

/** Two iron bands round the cluster. */
function bands(): THREE.BufferGeometry {
  return merged([
    new THREE.TorusGeometry(0.105, 0.013, 6, 18).rotateX(Math.PI / 2).translate(0, 0.07, 0),
    new THREE.TorusGeometry(0.125, 0.011, 6, 18).rotateX(Math.PI / 2).translate(0, 0.15, 0),
  ]);
}

function chargeParts({ id, size, crystals, weights }: ChargeShape): ChargeParts {
  const frame = [
    part(`${id}.plate`, plate(), SLATE, [0, 0, 0], { metalness: 0.35 }),
    part(`${id}.crystals`, cluster(crystals), VIOLET, [0, PLATE, 0], {
      roughness: 0.3,
      glow: VIOLET_GLOW,
      pulse: true,
    }),
    part(`${id}.bands`, bands(), IRON, [0, PLATE, 0], { metalness: 0.55, roughness: 0.4 }),
  ];
  if (weights > 0)
    frame.push(
      part(
        `${id}.weights`,
        around(() => new THREE.BoxGeometry(0.07, 0.06, 0.07), angles(weights, 0.3), 0.235),
        ORE_BLOCK,
        [0, 0.03, 0],
        { roughness: 0.8, metalness: 0 },
      ),
    );
  return {
    frame: frame.map((p) => sized(size, p)),
    heart: sized(
      size,
      part(
        `${id}.heart`,
        new THREE.CylinderGeometry(0.024, 0.03, 1, 6).translate(0, 0.5, 0),
        HEART,
        [0, PLATE, 0],
        { roughness: 0.2, glow: HEART_GLOW, glowStrength: 1.4 },
      ),
      true,
    ),
    heartFull: 0.3 * size,
    splinters: sized(
      size,
      part(
        `${id}.splinters`,
        around(() => new THREE.OctahedronGeometry(0.024).scale(0.6, 1.7, 0.6), angles(5), 0.21),
        SPLINTER,
        [0, PLATE + 0.17, 0],
        { roughness: 0.3, spin: 'y', speed: 2.6, glow: VIOLET_GLOW },
      ),
    ),
  };
}

/** The crystal charge: its own tile, stone and ore. */
export const CHARGE = chargeParts({ id: 'chg', size: 1.3, crystals: 5, weights: 0 });
/** The heavy crystal charge: its tile and the ring round it, basalt and gold too. */
export const HEAVY_CHARGE = chargeParts({ id: 'hchg', size: 1.65, crystals: 7, weights: 6 });
